import { relations } from 'drizzle-orm';
import {
  boolean,
  doublePrecision,
  index,
  integer,
  json,
  pgTable,
  text,
  uniqueIndex,
  varchar,
} from 'drizzle-orm/pg-core';
import { lower } from './utils';

export const userTable = pgTable(
  'users',
  {
    id: text().primaryKey().notNull(),
    created: integer().notNull(),
    updated: integer().notNull(),
    email: varchar({ length: 255 }).notNull(),
    name: varchar({ length: 255 }).notNull(),
    active: boolean().default(true),
    plan: text('plan'),
  },
  (table) => ({
    emailIndex: uniqueIndex('users_email_index').on(lower(table.email)),
  })
);

export const userRelations = relations(userTable, ({ one, many }) => ({
  profile: one(profileTable, {
    fields: [userTable.id],
    references: [profileTable.userId],
  }),
  pushDevices: many(pushDeviceTable),
  healthRaw: many(healthRawTable),
  posts: many(postTable),
  photos: many(photoTable),
}));

export const profileTable = pgTable(
  'profiles',
  {
    id: text().primaryKey().notNull(),
    created: integer().notNull(),
    updated: integer().notNull(),
    userId: text('user_id').notNull().unique(),
    name: text().notNull(),
    username: text(),
    isPublic: boolean('is_public').default(true).notNull(),
    email: text(),
    phone: text(),
    image: text(),
    overviewText: text('overview_text'),
    overviewContent: json('overview_content'),
    overviewHtml: text('overview_html'),
    collegeOffersText: text('college_offers_text'),
    collegeOffersContent: json('college_offers_content'),
    collegeOffersHtml: text('college_offers_html'),
    highlights: json(),
    location: text(),
    sport: text(),
    position: text(),
    position2: text(),
    sportEvent: text('sport_event'),
    team: text(),
    kitNumber: text('kit_number'),
    onboardingWeb: text('onboarding_web'),
    onboardingApp: text('onboarding_app'),
    dateOfBirth: integer('date_of_birth'),
    height: doublePrecision(),
    weight: doublePrecision(),
    twitter: text(),
    youtube: text(),
    instagram: text(),
    tiktok: text(),
    facebook: text(),
    gpa: text(),
    classYear: text('class_year'),
    fortyYardDash: doublePrecision('forty_yard_dash'),
    verticalJump: doublePrecision('vertical_jump'),
    broadJump: doublePrecision('broad_jump'),
    proAgility: doublePrecision('pro_agility'),
    gutCheck: doublePrecision('gut_check'),
    benchPress: doublePrecision('bench_press'),
    squat: doublePrecision(),
    powerClean: doublePrecision('power_clean'),
    hudlUrl: text('hudl_url'),
  },
  (table) => {
    return {
      userIdIndex: index('profiles_user_id_index').on(table.userId),
      usernameIndex: uniqueIndex('profiles_username_index').on(
        lower(table.username)
      ),
    };
  }
);

export const profileRelations = relations(profileTable, ({ one, many }) => ({
  user: one(userTable, {
    fields: [profileTable.userId],
    references: [userTable.id],
  }),
  posts: many(postTable),
  photos: many(photoTable),
}));

export const followTable = pgTable(
  'follows',
  {
    id: text().primaryKey().notNull(),
    created: integer().notNull(),
    updated: integer().notNull(),
    fromUserId: text('from_user_id').notNull(),
    toUserId: text('to_user_id').notNull(),
  },
  (table) => {
    return {
      fromUserIdIndex: index('follows_from_user_id_index').on(table.fromUserId),
      toUserIdIndex: index('follows_to_user_id_index').on(table.toUserId),
    };
  }
);

export const followRelations = relations(followTable, ({ one }) => ({
  fromUser: one(userTable, {
    fields: [followTable.fromUserId],
    references: [userTable.id],
  }),
  toUser: one(userTable, {
    fields: [followTable.toUserId],
    references: [userTable.id],
  }),
}));

export const recordingTable = pgTable('recordings', {
  id: text().primaryKey().notNull(),
  created: integer().notNull(),
  updated: integer().notNull(),
  userId: varchar('user_id', { length: 255 }).notNull(),
  userTimezone: varchar('user_timezone', { length: 255 })
    .default('UTC')
    .notNull(),
  userDate: integer('user_date').notNull(),
  prompt: text(),
});

export const logTable = pgTable(
  'logs',
  {
    id: text().primaryKey().notNull(),
    created: integer().notNull(),
    updated: integer().notNull(),
    date: integer().notNull(),
    userId: varchar('user_id', { length: 255 }).notNull(),
    recordId: varchar('record_id', { length: 255 }).notNull(),
    prompt: text().notNull(),
    category: varchar({ length: 255 }).notNull(),
    type: varchar({ length: 255 }).notNull(),
    disabled: boolean().default(false).notNull(),
    activityTypeId: varchar('activity_type_id', { length: 255 }),
    title: text(),
    description: text(),
    weightUnit: varchar('weight_unit', { length: 255 }),
    weight: doublePrecision(),
    weightKg: doublePrecision('weight_kg'),
    weightLbs: doublePrecision('weight_lbs'),
    weightSt: doublePrecision('weight_st'),
    distanceUnit: varchar('distance_unit', { length: 255 }),
    distance: doublePrecision(),
    durationSeconds: doublePrecision('duration_seconds'),
    count: integer(),
    calories: integer(),
  },
  (table) => {
    return {
      indexActivityTypeId: index('logs_index_activity_type_id').on(
        table.activityTypeId
      ),
    };
  }
);

export const logRelations = relations(logTable, ({ one, many }) => ({
  activityType: one(activityTypeTable, {
    fields: [logTable.activityTypeId],
    references: [activityTypeTable.id],
  }),
  logsBest: many(logBestTable),
}));

export const logBestTable = pgTable(
  'logs_best',
  {
    id: text().primaryKey().notNull(),
    created: integer().notNull(),
    updated: integer().notNull(),
    date: integer().notNull(),
    userId: varchar('user_id', { length: 255 }).notNull(),
    activityTypeId: varchar('activity_type_id', { length: 255 }).notNull(),
    logId: varchar('log_id', { length: 255 }).notNull(),
  },
  (table) => {
    return {
      userIdActivityTypeIdKey: uniqueIndex(
        'logs_best_user_id_activity_type_id_key'
      ).using(
        'btree',
        table.userId.asc().nullsLast().op('text_ops'),
        table.activityTypeId.asc().nullsLast().op('text_ops')
      ),
      logsBestIndexActivityTypeId: index(
        'logs_best_index_activity_type_id'
      ).using('btree', table.activityTypeId.asc().nullsLast().op('text_ops')),
      logsBestIndexLogId: index('logs_best_index_log_id').using(
        'btree',
        table.logId.asc().nullsLast().op('text_ops')
      ),
    };
  }
);

export const logBestRelations = relations(logBestTable, ({ one }) => ({
  log: one(logTable, {
    fields: [logBestTable.logId],
    references: [logTable.id],
  }),
  activityType: one(activityTypeTable, {
    fields: [logBestTable.activityTypeId],
    references: [activityTypeTable.id],
  }),
}));

export const nutritionTable = pgTable(
  'nutritions',
  {
    id: text().primaryKey().notNull(),
    created: integer().notNull(),
    updated: integer().notNull(),
    date: integer().notNull(),
    userId: varchar('user_id', { length: 255 }).notNull(),
    recordId: varchar('record_id', { length: 255 }).notNull(),
    prompt: text().notNull(),
    category: varchar({ length: 255 }).notNull(),
    type: varchar({ length: 255 }).notNull(),
    disabled: boolean().default(false).notNull(),
    nutritionTypeId: varchar('nutrition_type_id', { length: 255 }).notNull(),
    title: text(),
    description: text(),
    weightUnit: varchar('weight_unit', { length: 255 }),
    weight: doublePrecision(),
    weightKg: doublePrecision('weight_kg'),
    weightLbs: doublePrecision('weight_lbs'),
    weightSt: doublePrecision('weight_st'),
    durationSeconds: integer('duration_seconds'),
    count: integer(),
    calories: integer(),
  },
  (table) => {
    return {
      indexActivityTypeId: index('nutritions_index_nutrition_type_id').on(
        table.nutritionTypeId
      ),
    };
  }
);

export const nutritionRelations = relations(nutritionTable, ({ one }) => ({
  nutritionType: one(nutritionTypeTable, {
    fields: [nutritionTable.nutritionTypeId],
    references: [nutritionTypeTable.id],
  }),
}));

export const nutritionTypeTable = pgTable(
  'nutrition_types',
  {
    id: text().primaryKey().notNull(),
    created: integer().notNull(),
    updated: integer().notNull(),
    nameId: varchar('name_id', { length: 255 }).notNull(),
    name: varchar({ length: 255 }).notNull(),
  },
  (table) => {
    return {
      nameIdKey: uniqueIndex('nutrition_types_name_id_key').on(table.nameId),
    };
  }
);

export const nutritionTypeRelations = relations(
  nutritionTypeTable,
  ({ many }) => ({
    logs: many(logTable),
    logBest: many(logBestTable),
  })
);

export const activityTypeTable = pgTable(
  'activity_types',
  {
    id: text().primaryKey().notNull(),
    created: integer().notNull(),
    updated: integer().notNull(),
    nameId: varchar('name_id', { length: 255 }).notNull(),
    name: varchar({ length: 255 }).notNull(),
    goals: text(),
    category: varchar({ length: 255 }),
  },
  (table) => {
    return {
      nameIdKey: uniqueIndex('activity_types_name_id_key').using(
        'btree',
        table.nameId.asc().nullsLast().op('text_ops')
      ),
    };
  }
);

export const activityTypeRelations = relations(
  activityTypeTable,
  ({ many }) => ({
    logs: many(logTable),
    logBest: many(logBestTable),
  })
);

export const pushDeviceTable = pgTable(
  'push_devices',
  {
    id: text().primaryKey().notNull(),
    created: integer().notNull(),
    updated: integer().notNull(),
    userId: varchar('user_id', { length: 255 }).notNull(),
    token: text().notNull(),
  },
  (table) => {
    return {
      userIdIndex: index('push_devices_user_id_index').on(table.userId),
    };
  }
);

export const healthRawTable = pgTable(
  'health_raw',
  {
    id: text().primaryKey().notNull(),
    created: integer().notNull(),
    updated: integer().notNull(),
    userId: varchar('user_id', { length: 255 }).notNull(),
    type: varchar('type', { length: 255 }).notNull(),
    dateId: varchar('date_id', { length: 255 }).notNull(),
    data: json(),
  },
  (table) => {
    return {
      userIdIndex: index('health_raw_user_id_index').on(table.userId),
      userIdTypeDateIdIndex: uniqueIndex(
        'health_raw_user_id_type_date_id_index'
      ).on(table.userId, table.type, table.dateId),
    };
  }
);

export const photoTable = pgTable(
  'photos',
  {
    id: text().primaryKey().notNull(),
    created: integer().notNull(),
    updated: integer().notNull(),
    userId: varchar('user_id', { length: 255 }).notNull(),
    width: integer().notNull(),
    height: integer().notNull(),
    title: text(),
    description: text(),
  },
  (table) => {
    return {
      userIdIndex: index('photos_user_id_index').on(table.userId),
    };
  }
);

export const photoRelations = relations(photoTable, ({ one }) => ({
  user: one(userTable, {
    fields: [photoTable.userId],
    references: [userTable.id],
  }),
}));

export const videoTable = pgTable(
  'videos',
  {
    id: text().primaryKey().notNull(),
    created: integer().notNull(),
    updated: integer().notNull(),
    userId: varchar('user_id', { length: 255 }).notNull(),
    isReady: boolean('is_ready').notNull(),
    sourceId: text('source_id'),
    sourceKey: text('source_key'),
    title: text(),
    description: text(),
    thumbnail: text(),
    url: text(),
  },
  (table) => {
    return {
      userIdIndex: index('videos_user_id_index').on(table.userId),
    };
  }
);

export const videoRelations = relations(videoTable, ({ one }) => ({
  user: one(userTable, {
    fields: [videoTable.userId],
    references: [userTable.id],
  }),
}));

export const postTable = pgTable(
  'posts',
  {
    id: text().primaryKey().notNull(),
    created: integer().notNull(),
    updated: integer().notNull(),
    userId: varchar('user_id', { length: 255 }).notNull(),
    profileId: varchar('profile_id', { length: 255 }).notNull(),
    date: integer().notNull(),
    title: text(),
    text: text(),
    content: json(),
    html: text(),
    photos: json(),
    layoutId: text(),
  },
  (table) => {
    return {
      userIdIndex: index('posts_user_id_index').on(table.userId),
      profileIdIndex: index('posts_profile_id_index').on(table.profileId),
    };
  }
);

export const postRelations = relations(postTable, ({ one }) => ({
  user: one(userTable, {
    fields: [postTable.userId],
    references: [userTable.id],
  }),
  profile: one(profileTable, {
    fields: [postTable.profileId],
    references: [profileTable.id],
  }),
}));

export const dbActionTable = pgTable(
  'db_actions',
  {
    id: text().primaryKey().notNull(),
    created: integer().notNull(),
    table: varchar('table', { length: 255 }).notNull(),
    type: varchar('type', { length: 255 }).notNull(),
    uniqueColumn: varchar('unique_column', { length: 255 }).notNull(),
    data: json().notNull(),
  },
  (table) => {
    return {
      createdIndex: index('db_actions_created_index').on(table.created),
    };
  }
);

export const contractTable = pgTable(
  'contracts',
  {
    id: text().primaryKey().notNull(),
    created: integer().notNull(),
    updated: integer().notNull(),
    userId: varchar('user_id', { length: 255 }).notNull(),
    profileId: varchar('profile_id', { length: 255 }).notNull(),
    year: integer('year').notNull(),
    startDate: integer('start_date').notNull(),
    endDate: integer('end_date').notNull(),
    data: json().notNull(),
  },
  (table) => {
    return {
      userIdIndex: index('contracts_user_id_index').on(table.userId),
      profileIdIndex: index('contracts_profile_id_index').on(table.profileId),
      profileIdYearIndex: uniqueIndex('contracts_profile_id_year_index').on(
        table.profileId,
        table.year
      ),
    };
  }
);

export const teamTable = pgTable(
  'teams',
  {
    id: text().primaryKey().notNull(),
    created: integer().notNull(),
    updated: integer().notNull(),
    userId: text('user_id').notNull().unique(),
    name: text().notNull(),
    username: text().notNull(),
    logo: text(),
    overviewText: text('overview_text'),
    overviewContent: json('overview_content').$type<any>(),
    overviewHtml: text('overview_html'),
    sport: text(),
    location: text(),
    twitter: text(),
    youtube: text(),
    instagram: text(),
    tiktok: text(),
    facebook: text(),
    schedule: json().$type<any>(),
  },
  (table) => {
    return {
      usernameIndex: uniqueIndex('teams_username_index').on(
        lower(table.username)
      ),
    };
  }
);

export const teamRelations = relations(teamTable, ({ one }) => ({
  user: one(userTable, {
    fields: [teamTable.userId],
    references: [userTable.id],
  }),
}));

export const teamMemberTable = pgTable(
  'team_members',
  {
    id: text().primaryKey().notNull(),
    created: integer().notNull(),
    updated: integer().notNull(),
    teamId: text('team_id').notNull(),
    profileId: text('profile_id').notNull(),
    type: text().notNull(),
    tags: json().$type<string[]>(),
  },
  (table) => {
    return {
      teamIdIndex: index('team_members_team_id_index').on(table.teamId),
      profileIdIndex: index('team_members_profile_id_index').on(
        table.profileId
      ),
    };
  }
);

export const teamMemberRelations = relations(teamMemberTable, ({ one }) => ({
  team: one(teamTable, {
    fields: [teamMemberTable.teamId],
    references: [teamTable.id],
  }),
  profile: one(profileTable, {
    fields: [teamMemberTable.profileId],
    references: [profileTable.id],
  }),
}));

export const teamShareTable = pgTable(
  'team_shares',
  {
    id: text().primaryKey().notNull(),
    created: integer().notNull(),
    updated: integer().notNull(),
    userId: text('user_id').notNull(),
    teamId: text('team_id').notNull(),
    email: text().notNull(),
    messageText: text().notNull(),
    messageHtml: text().notNull(),
    messageContent: json('message_content').$type<any>().notNull(),
    tags: json().$type<string[]>(),
    positions: json().$type<string[]>(),
  },
  (table) => {
    return {
      userIdIndex: index('team_shares_user_id_index').on(table.userId),
      teamIdIndex: index('team_shares_team_id_index').on(table.teamId),
    };
  }
);

export const teamShareRelations = relations(teamShareTable, ({ one }) => ({
  team: one(teamTable, {
    fields: [teamShareTable.teamId],
    references: [teamTable.id],
  }),
}));

export const messageTable = pgTable(
  'messages',
  {
    id: text().primaryKey().notNull(),
    created: integer().notNull(),
    updated: integer().notNull(),
    tempId: text('temp_id').notNull(),
    userId: text('user_id').notNull(),
    toUserId: text('to_user_id').notNull(),
    toProfileId: text('to_profile_id').notNull(),
    text: text().notNull(),
    textContent: json('text_content').$type<any>(),
    textHtml: text('text_html'),
    deleted: integer(),
  },
  (table) => {
    return {
      userIdIndex: index('messages_user_id_index').on(table.userId),
      toUserIdIndex: index('messages_to_user_id_index').on(table.toUserId),
      toProfileIdIndex: index('messages_to_profile_id_index').on(
        table.toProfileId
      ),
    };
  }
);

export const messageRelations = relations(messageTable, ({ one }) => ({
  toUser: one(userTable, {
    fields: [messageTable.toUserId],
    references: [userTable.id],
  }),
  fromUser: one(userTable, {
    fields: [messageTable.userId],
    references: [userTable.id],
  }),
}));

export const tables = {
  user: userTable,
  profile: profileTable,
  follow: followTable,
  recording: recordingTable,
  log: logTable,
  logBest: logBestTable,
  activityType: activityTypeTable,
  pushDevice: pushDeviceTable,
  healthRaw: healthRawTable,
  post: postTable,
  photo: photoTable,
  video: videoTable,
  nutrition: nutritionTable,
  nutritionType: nutritionTypeTable,
  contract: contractTable,
  team: teamTable,
  teamMember: teamMemberTable,
  message: messageTable,
  teamShare: teamShareTable,
};

export const schema = {
  ...tables,

  userRelations,
  profileRelations,
  followRelations,
  logRelations,
  logBestRelations,
  activityTypeRelations,
  postRelations,
  photoRelations,
  videoRelations,
  nutritionRelations,
  nutritionTypeRelations,
  teamRelations,
  teamMemberRelations,
  messageRelations,
  teamShareRelations,
};

export type TableType = keyof typeof tables;
